import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CommonService } from '../../../api/services/common.service';
import { AuthService } from '../../../api/services/auth.service';
import { getAccessValue } from '../../../helpers/rbac.helper';

@Component({
  selector: 'app-card-type-five',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink],
  templateUrl: './card-type-five.component.html',
  styleUrl: './card-type-five.component.css',
})
export class CardTypeFiveComponent implements OnDestroy {
  private resizeObserver?: ResizeObserver;
  private scrollContainerElement?: HTMLElement;
  private checkTimer: any;

  @ViewChild('scrollContainer') set scrollContainer(
    ref: ElementRef | undefined
  ) {
    if (ref?.nativeElement) {
      this.scrollContainerElement = ref.nativeElement;
      this.setupResizeObserver(ref.nativeElement);
      this.scheduleArrowUpdates();
    } else {
      this.cleanupResizeObserver();
      this.scrollContainerElement = undefined;
    }
  }

  get scrollContainer(): ElementRef | undefined {
    return this.scrollContainerElement
      ? new ElementRef(this.scrollContainerElement)
      : undefined;
  }

  showLeftArrow = false;
  showRightArrow = true;
  fieldsData: any;
  containerWidth = '100%';
  resizeTimer: any;
  isLoading: boolean = false;
  skeletonItems = new Array(8).fill(null);
  searchInput: any = '';
  profileResponse: any;

  constructor(
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.getProfileDetails();
    this.loadData();
  }

  ngOnDestroy() {
    this.cleanupResizeObserver();
    if (this.checkTimer) clearTimeout(this.checkTimer);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }

  private setupResizeObserver(element: HTMLElement) {
    this.cleanupResizeObserver();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateArrows(element);
      });
      this.resizeObserver.observe(element);
      if (element.parentElement) {
        this.resizeObserver.observe(element.parentElement);
      }
    }
  }

  private cleanupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  private scheduleArrowUpdates() {
    this.updateArrows();
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => this.updateArrows());
    }
    [50, 150, 300, 600].forEach((delay) => {
      setTimeout(() => this.updateArrows(), delay);
    });
  }

  getProfileDetails() {
    let token = localStorage.getItem('accessToken');
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data.data;
        this.cdr.detectChanges();
      },
      error: () => {
        this.cdr.detectChanges();
      },
    });
  }

  canNavigate(): boolean {
    if (!this.profileResponse) return true;
    const access = getAccessValue(
      this.profileResponse,
      'control-task-operations',
      'all-tasks',
    );
    return access !== 'no_access';
  }

  navigateToTasks(item: any) {
    if (!this.canNavigate()) return;
    if (!item?.key?.framework) return;
    const storedView = sessionStorage.getItem('tasksView');
    const storedState = storedView ? JSON.parse(storedView) : {};

    const tasksView: any = {
      ...storedState,
      standardName: [item.key.framework],
      'mappedStandards.standardName': [item.key.framework],
    };

    sessionStorage.setItem('tasksView', JSON.stringify(tasksView));
    this.router.navigate(['/eciso/tasks']);
  }

  ngAfterViewInit() {
    this.scheduleArrowUpdates();
  }

  @HostListener('window:resize', [])
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.setContainerWidth();
      this.updateArrows();
    }, 200);
  }

  /**
   * Adjusts the container width dynamically based on screen size breakpoints.
   */
  setContainerWidth() {
    this.containerWidth = '100%';

    // Apply the width dynamically to the scroll container
    if (this.scrollContainerElement) {
      this.scrollContainerElement.style.width = this.containerWidth;
    }
    this.updateArrows();
  }

  scrollLeft(container?: HTMLElement) {
    const el = container || this.scrollContainerElement;
    if (!el) return;
    el.scrollBy({ left: -250, behavior: 'smooth' });
    setTimeout(() => this.updateArrows(el), 300);
  }

  scrollRight(container?: HTMLElement) {
    const el = container || this.scrollContainerElement;
    if (!el) return;
    el.scrollBy({ left: 250, behavior: 'smooth' });
    setTimeout(() => this.updateArrows(el), 300);
  }

  onScroll(container?: HTMLElement) {
    this.updateArrows(container);
  }

  updateArrows(container?: HTMLElement) {
    const el = container || this.scrollContainerElement;
    if (!el) return;

    const threshold = 2;
    const hasScroll = el.scrollWidth > el.clientWidth + threshold;
    const atStart = el.scrollLeft <= threshold;
    const atEnd =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold;

    this.showLeftArrow = hasScroll && !atStart;
    this.showRightArrow = hasScroll && !atEnd;
    this.cdr.detectChanges();
  }

  selectCard(selectedItem: any) {
    if (!this.fieldsData) return;
    this.fieldsData.forEach((item: any) => {
      if (item !== selectedItem) {
        item.mySelected = false;
      }
    });
    selectedItem.mySelected = !selectedItem.mySelected;
  }

  /** Function to load the task list with the formId */
  loadData() {
    this.isLoading = true;
    this.commonService
      .loadListWithOptions('6a3d163923bf07d3d695d993', { limit: 1000 })
      .subscribe({
        next: (res: any) => {
          const records = res?.data?.records ?? res?.data ?? [];
          const mapped = records.map((item: any) => ({
            ...item,
            key: {
              framework: item.shortName || item.name || '',
            },
            completedPercent: item.completedPercent ?? item.percentage ?? 0,
            logo: item.iconUrl || item.logo || '',
            mySelected: item.isSelected === true || item.isSelected === 'true',
          }));
          this.fieldsData = mapped.sort((a: any, b: any) => {
            const aSel = a.mySelected ? 1 : 0;
            const bSel = b.mySelected ? 1 : 0;
            return bSel - aSel;
          });
          this.isLoading = false;
          this.cdr.detectChanges();
          this.scheduleArrowUpdates();
        },
        error: (err) => {
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }
}

